// src/components/ResourceManager/Hardware/HardwareTableColumns.ts

import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { Hardware } from "./request/request";

// Icons
import { ReactComponent as IconEye } from "../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/IconEdit_KE.svg";
import { ReactComponent as IconDelete } from "../../../../shared/assets/Icons/Icon_Delete.svg";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericButton from "@ui-components/Button/GenericButton";

/**
 * Props que recibe getHardwareColumns para definir las acciones.
 */
interface HardwareColumnsProps {
	handleViewHardware: (hw: Hardware) => void;
	handleEditHardware: (hw: Hardware) => void;
	handleDeleteClick: (hw: Hardware) => void;
	/**
	 * If true, shows the "Data Center ID" column (when "All" is selected)
	 */
	showDatacenterColumn?: boolean;
}

/**
 * Retorna las columnas de la tabla para Hardware.
 */
export function getHardwareColumns({
	handleViewHardware,
	handleEditHardware,
	handleDeleteClick,
	showDatacenterColumn = false,
}: HardwareColumnsProps): GenericTableColumn<Hardware>[] {
	const columns: GenericTableColumn<Hardware>[] = [
		{
			header: "ID",
			accessor: "id",
			width: "80px",
		},
	];
	
	// Conditionally add Data Center ID column
	if (showDatacenterColumn) {
		columns.push({
			header: "Data Center ID",
			accessor: "datacenterId",
			width: "120px",
			renderCell: (row: Hardware) => (
				<div className="max-w-[120px] overflow-x-auto thin-scroll">
					<span className="whitespace-nowrap inline-block">{row.datacenterId || "-"}</span>
				</div>
			),
		});
	}
	
	columns.push(
		{
			header: "Hostname",
			accessor: "hostname",
			width: "200px",
			renderCell: (row: Hardware) => (
				<div className="max-w-[200px] overflow-x-auto thin-scroll">
					<span className="whitespace-nowrap inline-block">{row.hostname || "-"}</span>
				</div>
			),
		},
		{
			header: "Type + Subtype",
			width: "200px",
			renderCell: (row: Hardware) => {
				const typeName = row.typeHardware 
					? (typeof row.typeHardware === "object" ? row.typeHardware.name : row.typeHardware)
					: "";
				const subtypeName = row.subtypeHardware
					? (typeof row.subtypeHardware === "object" ? row.subtypeHardware.name : row.subtypeHardware)
					: "";
				const combined = [typeName, subtypeName].filter(Boolean).join(" + ");
				return (
					<div className="max-w-[200px] overflow-x-auto thin-scroll">
						<span className="whitespace-nowrap inline-block">{combined || "-"}</span>
					</div>
				);
			},
			sortAccessor: (row: Hardware) => {
				const typeName = row.typeHardware 
					? (typeof row.typeHardware === "object" ? row.typeHardware.name : row.typeHardware)
					: "";
				const subtypeName = row.subtypeHardware
					? (typeof row.subtypeHardware === "object" ? row.subtypeHardware.name : row.subtypeHardware)
					: "";
				return [typeName, subtypeName].filter(Boolean).join(" + ");
			},
		},
		{
			header: "Serial Number",
			accessor: "serialNumber",
			width: "150px",
			renderCell: (row: Hardware) => (
				<div className="max-w-[150px] overflow-x-auto thin-scroll">
					<span className="whitespace-nowrap inline-block">{row.serialNumber || "-"}</span>
				</div>
			),
		},
		{
			header: "Vendor",
			accessor: "vendor",
			width: "120px",
			renderCell: (row: Hardware) => (
				<div className="max-w-[120px] overflow-x-auto thin-scroll">
					<span className="whitespace-nowrap inline-block">{row.vendor || "-"}</span>
				</div>
			),
		},
		{
			header: "Status",
			width: "100px",
			renderCell: (row: Hardware) => {
				const status = row.status || "";
				const statusLower = status.toLowerCase();
				let variant: "success" | "error" | "default" | "warning" | "neutral" = "default";
				
				if (statusLower === "active") {
					variant = "success";
				} else if (statusLower === "inactive") {
					variant = "default";
				} else if (statusLower === "installed" || statusLower === "assigned") {
					variant = "neutral"; // brand variant
				} else if (statusLower === "provisioned") {
					variant = "warning";
				}
				
				return (
					<GenericTag
						variant={variant}
						label={status || "-"}
						size="small"
					/>
				);
			},
			sortAccessor: (row: Hardware) => row.status || "",
		},
		{
			header: "Actions",
			accessor: "id",
			width: "200px",
			renderCell: (row) => (
				<div className="flex items-center gap-2">
					{/* View */}
					<GenericButton
					variant="secondary"
					onClick={() => handleViewHardware(row)}
					icon={<IconEye className="w-5 h-5 text-semantic-text-secondary" />}
					/>

					{/* Edit */}
					<GenericButton
					variant="secondary"
					onClick={() => handleEditHardware(row)}
					icon={<IconEdit className="w-5 h-5 text-semantic-text-secondary" />}
					/>

					{/* Delete */}
					<GenericButton
						variant="danger"
						onClick={() => handleDeleteClick(row)}
						icon={<IconDelete className="w-6 h-6" />}
					/>
				</div>
			),
		}
	);
	
	return columns;
}
